import { api } from './api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TeamPermissions {
  profile: boolean;
  analytics: boolean;
  leads: boolean;
  collaborations: boolean;
}

export interface TeamMember {
  id: number;
  invite_email: string;
  permissions: TeamPermissions;
  status: 'pending' | 'active' | 'revoked';
  invited_at: string;
  accepted_at: string | null;
  granted_user_id: number | null;
  granted_user_name: string | null;
}

export interface SharedAccess {
  id: number;
  permissions: TeamPermissions;
  status: 'pending' | 'active' | 'revoked';
  invited_at: string;
  accepted_at: string | null;
  owner_id: number;
  owner_name: string;
  owner_email: string;
  profile_url: string | null;
  profile_image: string | null;
  tagline: string | null;
}

export interface ListTeamResponse {
  success: boolean;
  my_team: TeamMember[];
  shared_with_me: SharedAccess[];
}

export interface InvitePayload {
  email: string;
  permissions: TeamPermissions;
}

export interface InviteResponse {
  success: boolean;
  message: string;
  grant_id: number;
  is_registered: boolean;
}

export interface UpdatePermissionsResponse {
  success: boolean;
  message: string;
}

export interface RevokeResponse {
  success: boolean;
  message: string;
}

export interface AcceptDeclineResponse {
  success: boolean;
  message: string;
}

// Owner dashboard view (scoped by permissions)
export interface OwnerAnalytics {
  total_views: number;
  qr_views: number;
  direct_views: number;
  total_clicks: number;
}

export interface OwnerLead {
  id: number;
  name: string;
  email: string | null;
  mobile_number: string;
  subject: string;
  message: string;
  is_read: number;
  created_at: string;
  platform: string | null;
  location: string | null;
}

export interface OwnerCollaboration {
  id: number;
  name: string;
  description: string | null;
  logo: string | null;
  url: string | null;
  display_order: number;
}

export interface OwnerProfileData {
  id: number;
  name: string;
  tagline: string | null;
  bio: string | null;
  phone: string | null;
  secondary_phone: string | null;
  email: string | null;
  location: string | null;
  profile_image: string | null;
  profile_url: string;
  subscription_plan: string;
  social_links: Array<{ id: number; platform: string; label: string | null; url: string; display_order: number }>;
  custom_links: Array<{ id: number; label: string; icon: string; url: string; display_order: number }>;
  collaborations: OwnerCollaboration[];
}

export interface ViewOwnerDashboardResponse {
  success: boolean;
  owner: {
    id: number;
    name: string;
    email: string;
  };
  permissions: TeamPermissions;
  data: {
    profile?: OwnerProfileData;
    analytics?: OwnerAnalytics;
    leads?: OwnerLead[];
    collaborations?: OwnerCollaboration[];
  };
}

// ---------------------------------------------------------------------------
// Default empty permissions helper
// ---------------------------------------------------------------------------
export const defaultPermissions = (): TeamPermissions => ({
  profile: false,
  analytics: false,
  leads: false,
  collaborations: false,
});

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/** GET /team  – list my_team (as owner) + shared_with_me (as guest) */
export const listTeam = () =>
  api.get<ListTeamResponse>('/team');

/** POST /team/invite  – owner invites a user by email */
export const inviteMember = (payload: InvitePayload) =>
  api.post<InviteResponse>('/team/invite', payload);

/** PUT /team/{id}  – owner updates permissions for a grant */
export const updatePermissions = (grantId: number, permissions: TeamPermissions) =>
  api.put<UpdatePermissionsResponse>(`/team/${grantId}`, { permissions });

/** DELETE /team/{id}  – owner revokes a grant */
export const revokeAccess = (grantId: number) =>
  api.delete<RevokeResponse>(`/team/${grantId}`);

/** POST /team/{id}/accept  – invited user accepts */
export const acceptInvite = (grantId: number) =>
  api.post<AcceptDeclineResponse>(`/team/${grantId}/accept`);

/** POST /team/{id}/decline  – invited user declines */
export const declineInvite = (grantId: number) =>
  api.post<AcceptDeclineResponse>(`/team/${grantId}/decline`);

/** GET /team/view/{ownerUserId}  – invitee views owner's scoped dashboard */
export const viewOwnerDashboard = (ownerUserId: number) =>
  api.get<ViewOwnerDashboardResponse>(`/team/view/${ownerUserId}`);
